import { useTranslation } from 'react-i18next';
import { 
  formatOrderDate as baseFormatOrderDate,
  formatDetailedOrderDate as baseFormatDetailedOrderDate,
  formatPurchaseDateTime as baseFormatPurchaseDateTime,
  parseServerDate,
  DateFormatOptions
} from '../utils/dateUtils';

/**
 * Custom hook for date formatting that respects the app's current language
 * This ensures dates are displayed in the same language as the rest of the app
 * and updates reactively when the language changes
 */
export const useDateFormatter = () => {
  const { i18n } = useTranslation();

  // Map app language codes to locale codes
  const getLocaleFromLanguage = (language: string): string => {
    const localeMap: { [key: string]: string } = {
      'es': 'es-ES',
      'en': 'en-US', 
      'fr': 'fr-FR',
      'pt': 'pt-BR'
    };
    return localeMap[language] || 'es-ES';
  };

  // Get current locale - this will trigger re-renders when language changes
  const currentLocale = getLocaleFromLanguage(i18n.language);

  const formatOrderDate = (dateString: string, options: DateFormatOptions = {}) => {
    return baseFormatOrderDate(dateString, { ...options, locale: currentLocale });
  };

  const formatDetailedOrderDate = (dateString: string, options: DateFormatOptions = {}) => {
    return baseFormatDetailedOrderDate(dateString, { ...options, locale: currentLocale });
  };

  const formatPurchaseDateTime = (dateString: string, options: DateFormatOptions = {}) => {
    return baseFormatPurchaseDateTime(dateString, { ...options, locale: currentLocale });
  };

  const formatRelativeTime = (dateString: string): string => {
    const date = parseServerDate(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return i18n.language === 'es' ? 'Hace un momento' : 'Just now';
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      if (i18n.language === 'es') {
        return `Hace ${diffInMinutes} ${diffInMinutes === 1 ? 'minuto' : 'minutos'}`;
      }
      return `${diffInMinutes} ${diffInMinutes === 1 ? 'minute' : 'minutes'} ago`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      if (i18n.language === 'es') {
        return `Hace ${diffInHours} ${diffInHours === 1 ? 'hora' : 'horas'}`;
      }
      return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
      if (i18n.language === 'es') {
        return `Hace ${diffInDays} ${diffInDays === 1 ? 'día' : 'días'}`;
      }
      return `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`;
    }

    // For older dates, use formatted date (formatOrderDate already includes time)
    return formatOrderDate(dateString);
  };

  return {
    formatOrderDate,
    formatDetailedOrderDate,
    formatPurchaseDateTime,
    formatRelativeTime,
    currentLanguage: i18n.language,
    currentLocale
  };
};
